import { css, CSSObject } from 'styled-components';

import { useState, useLayoutEffect, useMemo, Fragment } from 'react';
import { usePopper } from 'react-popper';
import { Range, Editor } from 'slate';
import { useSlateStatic, ReactEditor } from 'slate-react';
import { Colors } from 'styles';

import { getOppositePlacement, some, hasMultipleInSelection } from '../utils';
import { usePlugins } from '../hooks';
import { BRACKET_TYPES } from '../constants';
import { HeadingLevel } from '../plugins/heading/constants';
import { HEADING_PLUGIN_ID } from '../plugins/heading/types';
import Divider from '@mui/material/Divider';
import type { AllPluginID } from '../plugins/types';
import { AUTOCORRECT_PLUGIN_ID } from '../plugins/autoCorrect/types';
import { isInsideListAtSelection } from '../plugins/list/utils';
import { areMultipleBracketsSelected } from '../utils/multipleBracketsSelected';
import { useMostRecentInput, RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { INPUT_TYPES, InputType } from 'domains/reporter/inputTypes';
import { useReporterState } from '../hooks/useReporter';
import { PICKLIST_PLUGIN_ID } from '../plugins/picklist/types';
import { find } from '../utils/find';
import { isSelectionContainedInNode } from '../utils/isSelectionContainedInNode';
import { CustomElement } from '../slate-custom-types';
import { telemetryContextFor } from 'modules/Apollo/types';
import { rootTheme } from 'theme/rootTheme';

/** *
 * Use this constant to comparatively set z-index for components that should sit above
 * this component.
 */
const Z_INDEX = 13;

export const getDisplayablePluginIDs = ({
  editor,
  pluginIDs: _pluginIDs,
  mostRecentInput,
  isMousePressed,
}: {
  editor: Editor;
  pluginIDs: Array<AllPluginID>;
  mostRecentInput: InputType;
  isMousePressed: boolean;
}): Array<AllPluginID> => {
  let pluginIDs = _pluginIDs;
  const selection = editor.selection;
  if (selection == null) {
    return pluginIDs;
  }

  // Hide the toolbar entirely while the mouse is pressed down
  if (isMousePressed === true) {
    return [];
  }

  if (areMultipleBracketsSelected(editor)) {
    return [];
  }

  if (hasMultipleInSelection(editor, 'paragraph')) {
    pluginIDs = pluginIDs.filter((pluginID) => pluginID !== AUTOCORRECT_PLUGIN_ID);
  }

  if (isInsideListAtSelection(editor)) {
    pluginIDs = pluginIDs.filter((pluginID) => pluginID !== HEADING_PLUGIN_ID);
  }

  // if selection spans multiple nodes of different types, hide toolbar
  const uniqueNodeTypes = new Set();
  for (const [node] of Array.from(Editor.nodes(editor, { at: selection }))) {
    if (!Editor.isEditor(node) && (node as CustomElement).type != null) {
      uniqueNodeTypes.add((node as CustomElement).type);
    }
  }
  if (uniqueNodeTypes.size > 1 && hasMultipleInSelection(editor, 'paragraph')) {
    return [];
  }

  // If we are inside a bracket, do not show heading buttons
  if (some(editor, (n) => BRACKET_TYPES.includes(n.type))) {
    pluginIDs = pluginIDs.filter((pluginID) => pluginID !== HEADING_PLUGIN_ID);
  }

  if (mostRecentInput !== INPUT_TYPES.MOUSE) {
    pluginIDs = [];
  }

  return pluginIDs;
};

export const HoveringToolbar = () => {
  const editor = useSlateStatic();
  const { getHoveringToolbarConfigs } = usePlugins();

  const activatedToolbarConfigs = useMemo(() => {
    return getHoveringToolbarConfigs()
      .map((config) => config())
      .flat();
  }, [getHoveringToolbarConfigs]);

  const [referenceElement, setReferenceElement] = useState<globalThis.Range | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const [displayableToolbarConfigs, setDisplayableToolbarConfigs] =
    useState(activatedToolbarConfigs);
  const { mostRecentInput, isMousePressed } = useMostRecentInput({
    id: RICH_TEXT_EDITOR,
    context: APOLLO_QUERY_CONTEXT,
  });
  const { variant } = useReporterState();
  const { styles, state } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'arrow', options: { element: arrowElement } },
    ],
  });

  useLayoutEffect(() => {
    const { selection } = editor;

    if (
      !selection ||
      !ReactEditor.isFocused(editor) ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ''
    ) {
      if (referenceElement != null) {
        setReferenceElement(null);
      }
      return;
    }

    // We do not allow you to directly edit a picklist inside the template or macro editors. You must edit
    // the options inside the pane. As a result, we hide the hovering toolbar when the selection is entirely contained within a picklist
    // entry. The hovering toolbar should instead be used inside the pane.
    if (['template', 'fragment'].includes(variant)) {
      const picklistEntry = find(editor, (n) => n.type === PICKLIST_PLUGIN_ID);
      if (
        picklistEntry != null &&
        isSelectionContainedInNode(editor, picklistEntry[1], selection)
      ) {
        if (referenceElement != null) {
          setReferenceElement(null);
        }
        return;
      }
    }

    // The DOM selection lags behind the editor selection.
    // We use requestAnimationFrame to sync with the browser's paint cycle
    // before updating the reference element position.
    requestAnimationFrame(() => {
      const domSelection = window.getSelection();
      if (domSelection && domSelection.rangeCount > 0 && !domSelection.isCollapsed) {
        const domRange = domSelection.getRangeAt(0);
        setReferenceElement(domRange);
      }

      const displayablePluginIDs: AllPluginID[] = getDisplayablePluginIDs({
        editor,
        pluginIDs: activatedToolbarConfigs.map((toolbarConfig) => toolbarConfig.pluginID),
        mostRecentInput: mostRecentInput as InputType,
        isMousePressed,
      });

      const displayableToolbarConfigs = activatedToolbarConfigs.filter((toolbarConfig) =>
        displayablePluginIDs.includes(toolbarConfig.pluginID)
      );

      if (displayableToolbarConfigs.length === 0) {
        setReferenceElement(null);
      } else {
        setDisplayableToolbarConfigs(displayableToolbarConfigs);
      }
    });
  }, [
    activatedToolbarConfigs,
    editor.selection,
    editor,
    mostRecentInput,
    referenceElement,
    variant,
    isMousePressed,
  ]);

  if (!referenceElement) return null;

  return (
    <div
      {...(state?.attributes?.popper != null ? state.attributes.popper : {})}
      data-hovering-toolbar
      ref={setPopperElement}
      css={css`
        ${css((styles.popper as CSSObject) || {})};
        background-color: ${Colors.gray3};
        padding: 5px 10px;
        border-radius: ${rootTheme.radius.xs}px;
        display: flex;
        align-items: center;
        z-index: ${Z_INDEX};
        &[data-popper-reference-hidden='true'] {
          visibility: hidden;
          pointer-events: none;
        }
      `}
    >
      {displayableToolbarConfigs.map(({ toolbarButton, pluginID, options }) => {
        const fragment = <Fragment key={pluginID}>{toolbarButton}</Fragment>;

        if (
          displayableToolbarConfigs.length > 1 &&
          ((pluginID === HEADING_PLUGIN_ID && options?.level === HeadingLevel.H2) ||
            pluginID === AUTOCORRECT_PLUGIN_ID)
        ) {
          return (
            <div
              key={pluginID + String(options?.level)}
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              {fragment}
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            </div>
          );
        }

        return fragment;
      })}

      <div
        ref={setArrowElement}
        css={css`
          ${css((styles.arrow as CSSObject) || {})};
          transform: ${styles.arrow?.transform} rotate(45deg);
          width: 10px;
          height: 10px;
          background-color: ${Colors.gray3};

          ${state?.placement
            ? css`
                ${getOppositePlacement(state?.placement)}: -5px;
              `
            : undefined}
        `}
      />
    </div>
  );
};

HoveringToolbar.displayName = 'HoveringToolbar';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(HoveringToolbar);
